/**
 * Полная логика генерации контуров коллекторов
 * Взято из FloorCollectorEditor.js (строки 4249-5061)
 * Поддерживает все типы фитингов: фланцы, накидные гайки, шайбы с наружной резьбой, обычные фитинги
 */

import fittingsData from '../fittingsData';
import {
  extractDnFromFlangeKey,
  extractSizeFromNakGaikaKey,
  extractSizeFromShaibaNarRezba,
  getPipeDiameterForDn,
  getPipeDiameterForNakGaika,
  getPipeDiameterForShaibaNarRezba,
  getShaibaKeyForFitting,
  getHoleDiameterForPipe,
  flangePipeInsertion,
  nakShaibaPipeMeasure,
  visibleSpecialStubLength,
  resolveNippelMuftaFitting,
} from './collectorUtils';
import {
  determineThickness,
  getAvailableThicknesses,
} from './pipeThicknessUtils';

// Счётчик для генерации уникальных ID
let idCounter = 0;
const generateUniqueId = () => {
  idCounter += 1;
  return `${Date.now()}-${idCounter}`;
};

/**
 * Вычисляет размер основной трубы (высоту для квадратных, диаметр для круглых)
 * @param {string} shape - форма трубы ('round' или 'square')
 * @param {number} mainPipeDiameter - диаметр для круглых труб
 * @param {Object} pipeSpec - спецификация для квадратных труб
 * @returns {number} - размер трубы
 */
const getMainPipeSize = (shape, mainPipeDiameter, pipeSpec) => {
  if (shape === 'square' && pipeSpec) {
    // Для листа берем первый размер (ширину), для профиля - второй (высоту)
    const sizes = pipeSpec.profile.split('x').map(s => parseInt(s));
    return pipeSpec.squareType === 'sheet' ? sizes[0] : sizes[1];
  }
  return mainPipeDiameter;
};

/**
 * Генерирует контур с фланцем (свободным или приварным)
 * @param {Object} params - параметры контура
 * @returns {Object} - объект с cylinders, fittings, welds, circuitObjData
 */
export const generateFlangeCircuit = (params) => {
  const {
    circuit,
    circuitX,
    startY,
    mainPipeId,
    mainPipeDiameter,
    mainPipeThickness, // Реальная толщина основной трубы
    pipeSpec,
    index,
    direction = 'up', // 'up' или 'down'
    shape = 'round',
    isAdditionalOutlet = false,
    isThroughHole = false,
  } = params;

  const cylinders = [];
  const fittings = [];
  const welds = [];

  const fittingInfo = fittingsData[circuit.fittingId];
  if (!fittingInfo) return { cylinders, fittings, welds, circuitObjData: null };

  const isFlange = circuit.fittingId?.startsWith('Flanec_');
  const isFreeFlange = isFlange && !circuit.fittingId?.endsWith('_welded');
  
  const dn = extractDnFromFlangeKey(circuit.fittingId);
  const flangePipeDiameter = getPipeDiameterForDn(dn) || fittingInfo.diameter;
  const flangePipeLength = Number(circuit.flangePipeLength) || 100;
  const flangeLength = fittingInfo.length;
  const flangeDiameter = fittingInfo.diameter;
  
  const directionMultiplier = direction === 'up' ? -1 : 1;
  const rotation = direction === 'up' ? 0 : 180;
  
  // Начальная точка
  const mainPipeSize = getMainPipeSize(shape, mainPipeDiameter, pipeSpec);
  let currentY = direction === 'up' ? startY : startY + mainPipeSize;
  
  // Бортшайба для свободных фланцев
  let bortshaibaLength = 0;
  if (isFreeFlange) {
    const bortshaibaKey = `Bortshaiba_${dn}`;
    const bortshaibaInfo = fittingsData[bortshaibaKey];
    if (bortshaibaInfo) {
      bortshaibaLength = bortshaibaInfo.length || 10;
    }
  }
  
  // Длина трубки
  const actualPipeLength = isFreeFlange
    ? Math.max(0, flangePipeLength - bortshaibaLength)
    : flangePipeLength;
  
  // 1. Добавляем трубку
  const circuitPipeId = `cyl-circuit-flange-${index + 1}-${generateUniqueId()}`;
  
  if (actualPipeLength > 0) {
    // Определяем толщину промежуточной трубы:
    // Если у нас есть реальная толщина основной трубы, используем её как лимит
    // Иначе используем старую логику (минимальная толщина)
    let pipeThickness;
    if (mainPipeThickness) {
      // Получаем числовое значение толщины коллектора (например, 'x3' -> 3)
      const mainThicknessNum = parseFloat(mainPipeThickness.replace('x', ''));
      
      // Получаем доступные толщины для промежуточной трубы
      let availableThicknesses = getAvailableThicknesses(flangePipeDiameter, 'Матовый');
      
      // Фильтруем толщины ≤ толщины коллектора
      const validThicknesses = availableThicknesses.filter(t => {
        const num = parseFloat(t.replace('x', ''));
        return num <= mainThicknessNum;
      });
      
      // Берем максимальную из подходящих
      pipeThickness = validThicknesses.length > 0 
        ? validThicknesses[validThicknesses.length - 1]
        : availableThicknesses[0] || 'x2';
    } else {
      // Старая логика (для обратной совместимости)
      pipeThickness = determineThickness(mainPipeDiameter, flangePipeDiameter);
    }
    
    cylinders.push({
      id: circuitPipeId,
      x: circuitX - flangePipeDiameter / 2,
      y: direction === 'up' ? startY - actualPipeLength : currentY,
      width: flangePipeDiameter,
      height: actualPipeLength,
      pipeSpec: {
        shape: 'round',
        diameter: flangePipeDiameter,
        thickness: pipeThickness,
        coating: 'Матовый',
      },
      // Глубина вставки патрубка фланца в основную трубу (для производственной длины)
      insertionDepth: flangePipeInsertion(mainPipeDiameter, flangePipeDiameter, shape),
    });
    welds.push({
      id: `weld-circuit-flange-pipe-${index + 1}-${generateUniqueId()}`,
      x: circuitX,
      y: currentY,
      objects: [mainPipeId, circuitPipeId],
    });
    currentY = currentY + directionMultiplier * actualPipeLength;
  }
  
  // 2. Добавляем бортшайбу (для свободных фланцев)
  let bortshaibaId = null;
  if (isFreeFlange) {
    const bortshaibaKey = `Bortshaiba_${dn}`;
    const bortshaibaInfo = fittingsData[bortshaibaKey];
    if (bortshaibaInfo) {
      bortshaibaId = `fitting-circuit-bortshaiba-${index + 1}-${generateUniqueId()}`;
      fittings.push({
        id: bortshaibaId,
        key: bortshaibaKey,
        itemKey: bortshaibaKey,
        x: 0,
        y: 0,
        width: bortshaibaInfo.diameter,
        height: bortshaibaLength,
        snapPoints: bortshaibaInfo.snapPoints,
        correctionOffset: 3,
        useOriginalSize: true,
        groupX: circuitX,
        groupY: currentY,
        rotation: rotation,
        offsetX: bortshaibaInfo.diameter / 2,
        offsetY: bortshaibaLength,
        igesParentCylinderId: mainPipeId, // IGES: ID основной трубы
        isLongCircuit: circuit.isLongCircuit || false, // IGES: флаг длинного патрубка
        igesCreateHole: true, // IGES: этот фитинг создаёт отверстие на основной трубе
        holeDiameter: getHoleDiameterForPipe(flangePipeDiameter, { isSquare: shape === 'square', isThroughHole: isAdditionalOutlet ? isThroughHole : (circuit.isLongCircuit || false) }), // IGES: диаметр отверстия (особая логика для муфты 1/2")
        igesHoleDirection: direction, // IGES: направление отверстия ('up' или 'down')
      });
      currentY = currentY + directionMultiplier * bortshaibaLength;
    }
  }
  
  // 3. Добавляем фланец (надет на трубку)
  const flangeId = `fitting-circuit-flange-${index + 1}-${generateUniqueId()}`;
  const flangeY = direction === 'up' 
    ? startY - actualPipeLength + flangeLength
    : startY + mainPipeSize + actualPipeLength - flangeLength;
  
  fittings.push({
    id: flangeId,
    key: circuit.fittingId,
    itemKey: circuit.fittingId,
    x: 0,
    y: 0,
    width: flangeDiameter,
    height: flangeLength,
    groupX: circuitX,
    groupY: flangeY,
    rotation: rotation,
    offsetX: flangeDiameter / 2,
    offsetY: flangeLength,
    igesParentCylinderId: mainPipeId, // IGES: ID основной трубы
    isLongCircuit: circuit.isLongCircuit || false, // IGES: флаг длинного патрубка
    igesCreateHole: !isFreeFlange, // IGES: для приварных фланцев (без бортшайбы) создаёт отверстие
    holeDiameter: getHoleDiameterForPipe(flangePipeDiameter, { isSquare: shape === 'square', isThroughHole: isAdditionalOutlet ? isThroughHole : (circuit.isLongCircuit || false) }), // IGES: диаметр отверстия (особая логика для муфты 1/2")
    igesHoleDirection: direction, // IGES: направление отверстия ('up' или 'down')
  });
  
  // Сохраняем данные для размерных линий
  const circuitObjData = {
    id: circuitPipeId,
    type: 'cylinder',
    x: circuitX - flangePipeDiameter / 2,
    y: direction === 'up' ? startY - flangePipeLength : startY + mainPipeSize,
    width: flangePipeDiameter,
    height: flangePipeLength,
    centerX: circuitX,
    isFlange: true,
    isFreeFlange: isFreeFlange,
    flangeId: flangeId,
    topObjectId: direction === 'up' 
      ? (isFreeFlange && bortshaibaId ? bortshaibaId : flangeId)
      : circuitPipeId,
    topObjectType: direction === 'up' ? 'fitting' : 'cylinder',
    bottomObjectId: direction === 'up'
      ? circuitPipeId
      : (isFreeFlange && bortshaibaId ? bortshaibaId : flangeId),
    bottomObjectType: direction === 'up' ? 'cylinder' : 'fitting',
    bottomAttachSide: direction === 'down' ? 'top' : undefined,
    direction: direction, // Добавляем направление
  };
  
  return { cylinders, fittings, welds, circuitObjData };
};

/**
 * Генерирует контур с накидной гайкой
 * @param {Object} params - параметры контура
 * @returns {Object} - объект с cylinders, fittings, welds, circuitObjData
 */
export const generateNakGaikaCircuit = (params) => {
  const {
    circuit,
    circuitX,
    startY,
    mainPipeId,
    mainPipeDiameter,
    pipeSpec,
    index,
    direction = 'up',
    shape = 'round',
    isAdditionalOutlet = false,
    isThroughHole = false,
  } = params;

  const cylinders = [];
  const fittings = [];
  const welds = [];

  const fittingInfo = fittingsData[circuit.fittingId];
  if (!fittingInfo) return { cylinders, fittings, welds, circuitObjData: null };

  const size = extractSizeFromNakGaikaKey(circuit.fittingId);
  const nakGaikaPipeDiameter = getPipeDiameterForNakGaika(size);
  const lengthIsVisible = circuit.lengthIsVisible === true;
  const nakGaikaPipeLength = nakShaibaPipeMeasure(circuit.flangePipeLength, 0, shape, false, lengthIsVisible).visible;
  const nakGaikaLength = fittingInfo.length;
  const nakGaikaDiameter = fittingInfo.diameter;
  
  const directionMultiplier = direction === 'up' ? -1 : 1;
  const rotation = direction === 'up' ? 0 : 180;
  
  const mainPipeSize = getMainPipeSize(shape, mainPipeDiameter, pipeSpec);
  let currentY = direction === 'up' ? startY : startY + mainPipeSize;
  
  // Шайба накидной гайки
  const shaibaKey = getShaibaKeyForFitting(circuit.fittingId);
  const shaibaInfo = fittingsData[shaibaKey];
  const shaibaLength = shaibaInfo?.length || 10;
  
  // Длина трубки
  const nakMeasure = nakShaibaPipeMeasure(circuit.flangePipeLength, shaibaLength, shape, false, lengthIsVisible);
  const actualPipeLength = nakMeasure.outside;
  
  // 1. Добавляем трубку
  const circuitPipeId = `cyl-circuit-nakgaika-${index + 1}-${generateUniqueId()}`;
  
  if (actualPipeLength > 0) {
    // Определяем доступное покрытие для данного диаметра и толщины
    const { getActualCoating } = require('./pipeThicknessUtils');
    const actualCoating = getActualCoating(nakGaikaPipeDiameter, 'x3', 'Матовый');
    
    cylinders.push({
      id: circuitPipeId,
      x: circuitX - nakGaikaPipeDiameter / 2,
      y: direction === 'up' ? startY - actualPipeLength : currentY,
      width: nakGaikaPipeDiameter,
      height: actualPipeLength,
      pipeSpec: {
        shape: 'round',
        diameter: nakGaikaPipeDiameter,
        thickness: 'x3', // Фиксированная толщина 3мм для промежуточных трубок накидных гаек
        coating: actualCoating,
      },
      insertionDepth: nakMeasure.insertion,
    });
    welds.push({
      id: `weld-circuit-nakgaika-pipe-${index + 1}-${generateUniqueId()}`,
      x: circuitX,
      y: currentY,
      objects: [mainPipeId, circuitPipeId],
    });
    currentY = currentY + directionMultiplier * actualPipeLength;
  }
  
  // 2. Добавляем шайбу накидной гайки
  let shaibaId = null;
  if (shaibaInfo) {
    shaibaId = `fitting-circuit-shaiba-${index + 1}-${generateUniqueId()}`;
    
    fittings.push({
      id: shaibaId,
      key: shaibaKey,
      itemKey: shaibaKey,
      x: 0,
      y: 0,
      width: shaibaInfo.diameter,
      height: shaibaLength,
      groupX: circuitX,
      groupY: currentY,
      rotation: rotation,
      offsetX: shaibaInfo.diameter / 2,
      offsetY: shaibaLength,
      igesParentCylinderId: mainPipeId, // IGES: ID основной трубы
      isLongCircuit: circuit.isLongCircuit || false, // IGES: флаг длинного патрубка
      igesCreateHole: true, // IGES: этот фитинг создаёт отверстие на основной трубе
      holeDiameter: getHoleDiameterForPipe(nakGaikaPipeDiameter, { isSquare: shape === 'square', isThroughHole: isAdditionalOutlet ? isThroughHole : (circuit.isLongCircuit || false) }), // IGES: диаметр отверстия (особая логика для муфты 1/2")
      igesHoleDirection: direction, // IGES: направление отверстия ('up' или 'down')
    });
    currentY = currentY + directionMultiplier * shaibaLength;
  }
  
  // 3. Добавляем накидную гайку
  const nakGaikaId = `fitting-circuit-nakgaika-${index + 1}-${generateUniqueId()}`;
  const nakGaikaY = direction === 'up'
    ? startY - actualPipeLength + nakGaikaLength
    : startY + mainPipeSize + actualPipeLength - nakGaikaLength;
  
  fittings.push({
    id: nakGaikaId,
    key: circuit.fittingId,
    itemKey: circuit.fittingId,
    x: 0,
    y: 0,
    width: nakGaikaDiameter,
    height: nakGaikaLength,
    groupX: circuitX,
    groupY: nakGaikaY,
    rotation: rotation,
    offsetX: nakGaikaDiameter / 2,
    offsetY: nakGaikaLength,
    igesParentCylinderId: mainPipeId, // IGES: ID основной трубы
    isLongCircuit: circuit.isLongCircuit || false, // IGES: флаг длинного патрубка
    igesCreateHole: false, // IGES: отверстие уже создано шайбой
    igesHoleDirection: direction, // IGES: направление отверстия ('up' или 'down')
  });
  
  // Сохраняем данные для размерных линий
  // height должен быть равен nakGaikaPipeLength (трубка + шайба накидной гайки),
  // БЕЗ учета самой накидной гайки, так как она надевается на трубку
  const circuitObjData = {
    id: circuitPipeId,
    type: 'cylinder',
    x: circuitX - nakGaikaPipeDiameter / 2,
    y: direction === 'up' ? startY - nakGaikaPipeLength : startY + mainPipeSize,
    width: nakGaikaPipeDiameter,
    height: nakGaikaPipeLength,
    centerX: circuitX,
    isNakGaika: true,
    nakGaikaId: nakGaikaId,
    topObjectId: direction === 'up' ? shaibaId : circuitPipeId,
    topObjectType: direction === 'up' ? 'fitting' : 'cylinder',
    bottomObjectId: direction === 'up' ? circuitPipeId : shaibaId,
    bottomObjectType: direction === 'up' ? 'cylinder' : 'fitting',
    bottomAttachSide: direction === 'down' ? 'top' : undefined,
    direction: direction, // Добавляем направление
  };
  
  return { cylinders, fittings, welds, circuitObjData };
};

/**
 * Генерирует контур с шайбой с наружной резьбой
 * @param {Object} params - параметры контура
 * @returns {Object} - объект с cylinders, fittings, welds, circuitObjData
 */
export const generateShaibaNarRezbaCircuit = (params) => {
  const {
    circuit,
    circuitX,
    startY,
    mainPipeId,
    mainPipeDiameter,
    pipeSpec,
    index,
    direction = 'up',
    shape = 'round',
    isAdditionalOutlet = false,
    isThroughHole = false,
  } = params;

  const cylinders = [];
  const fittings = [];
  const welds = [];

  const fittingInfo = fittingsData[circuit.fittingId];
  if (!fittingInfo) return { cylinders, fittings, welds, circuitObjData: null };

  const size = extractSizeFromShaibaNarRezba(circuit.fittingId);
  const shaibaPipeDiameter = getPipeDiameterForShaibaNarRezba(size);
  const lengthIsVisible = circuit.lengthIsVisible === true;
  const shaibaPipeLength = nakShaibaPipeMeasure(circuit.flangePipeLength, 0, shape, true, lengthIsVisible).visible;
  const shaibaLength = fittingInfo.length;
  const shaibaDiameter = fittingInfo.diameter;
  
  const directionMultiplier = direction === 'up' ? -1 : 1;
  const rotation = direction === 'up' ? 0 : 180;
  
  const mainPipeSize = getMainPipeSize(shape, mainPipeDiameter, pipeSpec);
  let currentY = direction === 'up' ? startY : startY + mainPipeSize;
  
  // Длина трубки
  const shaibaMeasure = nakShaibaPipeMeasure(circuit.flangePipeLength, shaibaLength, shape, true, lengthIsVisible);
  const actualPipeLength = shaibaMeasure.outside;
  
  // 1. Добавляем трубку
  const circuitPipeId = `cyl-circuit-shaiba-${index + 1}-${generateUniqueId()}`;
  
  if (actualPipeLength > 0) {
    // Определяем доступное покрытие для данного диаметра и толщины
    const { getActualCoating } = require('./pipeThicknessUtils');
    const actualCoating = getActualCoating(shaibaPipeDiameter, 'x3', 'Матовый');
    
    cylinders.push({
      id: circuitPipeId,
      x: circuitX - shaibaPipeDiameter / 2,
      y: direction === 'up' ? startY - actualPipeLength : currentY,
      width: shaibaPipeDiameter,
      height: actualPipeLength,
      pipeSpec: {
        shape: 'round',
        diameter: shaibaPipeDiameter,
        thickness: 'x3', // Фиксированная толщина 3мм для промежуточных трубок шайб с резьбой
        coating: actualCoating,
      },
      insertionDepth: shaibaMeasure.insertion,
    });
    welds.push({
      id: `weld-circuit-shaiba-pipe-${index + 1}-${generateUniqueId()}`,
      x: circuitX,
      y: currentY,
      objects: [mainPipeId, circuitPipeId],
    });
    currentY = currentY + directionMultiplier * actualPipeLength;
  }
  
  // 2. Добавляем шайбу с наружной резьбой
  const shaibaId = `fitting-circuit-shaiba-nar-rezba-${index + 1}-${generateUniqueId()}`;
  
  fittings.push({
    id: shaibaId,
    key: circuit.fittingId,
    itemKey: circuit.fittingId,
    x: 0,
    y: 0,
    width: shaibaDiameter,
    height: shaibaLength,
    groupX: circuitX,
    groupY: currentY,
    rotation: rotation,
    offsetX: shaibaDiameter / 2,
    offsetY: shaibaLength,
    igesParentCylinderId: mainPipeId, // IGES: ID основной трубы
    isLongCircuit: circuit.isLongCircuit || false, // IGES: флаг длинного патрубка
    igesCreateHole: true, // IGES: этот фитинг создаёт отверстие на основной трубе
    holeDiameter: getHoleDiameterForPipe(shaibaPipeDiameter, { isSquare: shape === 'square', isThroughHole: isAdditionalOutlet ? isThroughHole : (circuit.isLongCircuit || false) }), // IGES: диаметр отверстия (особая логика для муфты 1/2")
    igesHoleDirection: direction, // IGES: направление отверстия ('up' или 'down')
  });
  currentY = currentY + directionMultiplier * shaibaLength;
  
  // Сохраняем данные для размерных линий
  const circuitObjData = {
    id: circuitPipeId,
    type: 'cylinder',
    x: circuitX - shaibaPipeDiameter / 2,
    y: direction === 'up'
      ? startY - shaibaPipeLength
      : startY + mainPipeSize,
    width: shaibaPipeDiameter,
    height: shaibaPipeLength,
    centerX: circuitX,
    isShaibaNarRezba: true,
    shaibaId: shaibaId,
    topObjectId: direction === 'up' ? shaibaId : circuitPipeId,
    topObjectType: direction === 'up' ? 'fitting' : 'cylinder',
    bottomObjectId: direction === 'up' ? circuitPipeId : shaibaId,
    bottomObjectType: direction === 'up' ? 'cylinder' : 'fitting',
    bottomAttachSide: direction === 'down' ? 'top' : undefined,
    direction: direction, // Добавляем направление
  };
  
  return { cylinders, fittings, welds, circuitObjData };
};

/**
 * Генерирует контур с обычным фитингом
 * @param {Object} params - параметры контура
 * @returns {Object} - объект с cylinders, fittings, welds, circuitObjData
 */
export const generateRegularFittingCircuit = (params) => {
  const {
    circuit,
    circuitX,
    startY,
    mainPipeId,
    mainPipeDiameter,
    pipeSpec,
    index,
    direction = 'up',
    shape = 'round',
    isAdditionalOutlet = false,
    isThroughHole = false,
  } = params;

  const cylinders = [];
  const fittings = [];
  const welds = [];

  const fittingInfo = fittingsData[circuit.fittingId];
  if (!fittingInfo) return { cylinders, fittings, welds, circuitObjData: null };

  const catalogLength = fittingInfo.length;
  let fLength;
  let dimensionOverride = {};

  const nippelMuftaResolved = resolveNippelMuftaFitting({
    fittingId: circuit.fittingId,
    fittingLength: circuit.fittingLength ?? null,
    pipeDiameter: mainPipeDiameter,
    catalogLength,
    shape,
    fittingLengthIsProduction: circuit.fittingLengthIsProduction === true,
  });

  if (nippelMuftaResolved) {
    fLength = nippelMuftaResolved.length;
    dimensionOverride = nippelMuftaResolved.dimensionOverride;
  } else if (circuit.fittingLength !== null && circuit.fittingLength !== undefined) {
    fLength = Number(circuit.fittingLength);
  } else {
    fLength = catalogLength;
  }

  const fDiameter = fittingInfo.diameter;
  const fittingId = `fitting-circuit-${index + 1}-${generateUniqueId()}`;
  
  const rotation = direction === 'up' ? 0 : 180;
  const mainPipeSize = getMainPipeSize(shape, mainPipeDiameter, pipeSpec);
  const groupY = direction === 'up' ? startY : startY + mainPipeSize;
  
  fittings.push({
    id: fittingId,
    key: circuit.fittingId,
    itemKey: circuit.fittingId,
    x: 0,
    y: 0,
    width: fDiameter,
    height: fLength,
    groupX: circuitX,
    groupY: groupY,
    rotation: rotation,
    offsetX: fDiameter / 2,
    offsetY: fLength,
    ...dimensionOverride,
    isLongCircuit: circuit.isLongCircuit || false, // IGES: флаг длинного патрубка
    igesParentCylinderId: mainPipeId, // IGES: ID основной трубы для правильной привязки
    igesCreateHole: true, // IGES: этот фитинг создаёт отверстие на основной трубе
    holeDiameter: getHoleDiameterForPipe(fDiameter, { isSquare: shape === 'square', isThroughHole: isAdditionalOutlet ? isThroughHole : (circuit.isLongCircuit || false) }), // IGES/DXF: диаметр отверстия (особая логика для муфты 1/2")
    igesHoleDirection: direction, // IGES: направление отверстия ('up' или 'down')
  });
  
  welds.push({
    id: `weld-circuit-${index + 1}-${generateUniqueId()}`,
    x: circuitX,
    y: groupY,
    objects: [mainPipeId, fittingId],
  });
  
  // Сохраняем данные для размерных линий
  // Для up: y = верхняя точка контура (startY - fLength)
  // Для down: y = низ коллектора (startY + mainPipeSize), height = длина контура
  const circuitObjData = {
    id: fittingId,
    type: 'fitting',
    x: circuitX - fDiameter / 2,
    y: direction === 'up' ? startY - fLength : startY + mainPipeSize,
    width: fDiameter,
    height: fLength,
    centerX: circuitX,
    direction: direction,
  };
  
  return { cylinders, fittings, welds, circuitObjData };
};

/**
 * Генерирует контур с трубой
 * @param {Object} params - параметры контура
 * @returns {Object} - объект с cylinders, fittings, welds, circuitObjData
 */
export const generatePipeCircuit = (params) => {
  const {
    circuit,
    circuitX,
    startY,
    mainPipeId,
    mainPipeDiameter,
    pipeSpec,
    index,
    direction = 'up',
    shape = 'round',
  } = params;

  const cylinders = [];
  const fittings = [];
  const welds = [];

  const circuitId = `cyl-circuit-${index + 1}-${generateUniqueId()}`;
  const pipeLength = Number(circuit.length) || 80;
  const pipeDiameter = Number(circuit.diameter) || 26.9;
  
  const mainPipeSize = getMainPipeSize(shape, mainPipeDiameter, pipeSpec);
  const groupY = direction === 'up' ? startY : startY + mainPipeSize;
  
  cylinders.push({
    id: circuitId,
    x: circuitX - pipeDiameter / 2,
    y: direction === 'up' ? startY - pipeLength : groupY,
    width: pipeDiameter,
    height: pipeLength,
    pipeSpec: {
      shape: 'round',
      diameter: pipeDiameter,
      thickness: determineThickness(pipeDiameter, mainPipeDiameter),
      coating: 'Матовый',
    },
  });
  
  welds.push({
    id: `weld-circuit-${index + 1}-${generateUniqueId()}`,
    x: circuitX,
    y: groupY,
    objects: [mainPipeId, circuitId],
  });
  
  // Сохраняем данные для размерных линий
  const circuitObjData = {
    id: circuitId,
    type: 'cylinder',
    x: circuitX - pipeDiameter / 2,
    y: direction === 'up' ? startY - pipeLength : startY + mainPipeSize,
    width: pipeDiameter,
    height: pipeLength,
    centerX: circuitX,
    direction: direction, // Добавляем направление
  };
  
  return { cylinders, fittings, welds, circuitObjData };
};

/**
 * Генерирует один контур (главная функция)
 * Автоматически выбирает нужный тип генерации
 * @param {Object} params - параметры контура
 * @returns {Object} - объект с cylinders, fittings, welds, circuitObjData
 */
export const generateCircuitFull = (params) => {
  const { circuit } = params;
  
  if (circuit.type === 'pipe') {
    return generatePipeCircuit(params);
  }
  
  if (!circuit.fittingId) {
    return { cylinders: [], fittings: [], welds: [], circuitObjData: null };
  }
  
  if (circuit.fittingId.startsWith('Flanec_')) {
    return generateFlangeCircuit(params);
  }
  
  if (circuit.fittingId.startsWith('Nak_Gaika_')) {
    return generateNakGaikaCircuit(params);
  }
  
  if (circuit.fittingId.startsWith('Shaib_Nar_Rezba_')) {
    return generateShaibaNarRezbaCircuit(params);
  }
  
  return generateRegularFittingCircuit(params);
};

/**
 * Генерирует массив контуров для одного коллектора
 * @param {Object} params - параметры
 * @returns {Object} - объект с cylinders, fittings, welds, circuitObjects
 */
export const generateCircuits = (params) => {
  const {
    circuits,
    startX,
    startY,
    mainPipeId,
    mainPipeDiameter,
    mainPipeThickness, // Реальная толщина основной трубы
    mainPipeProfile, // профиль для квадратных (например '80x80')
    pipeSpec, // полная спецификация трубы для квадратных
    firstCircuitOffset,
    circuitSpacing,
    direction = 'up',
    shape = 'round', // форма коллектора
    isAdditionalOutlet = false, // флаг что это дополнительный выход
    outletType = 'short', // тип дополнительного выхода: 'short' или 'through'
    pipeSpacing = 0, // межосевое расстояние между коллекторами
  } = params;

  const cylinders = [];
  const fittings = [];
  const welds = [];
  const circuitObjects = [];

  // Определяем флаг для сквозного отверстия (для дополнительных выходов)
  const isThroughHole = isAdditionalOutlet ? (outletType === 'through') : false;

  // Вычисляем позиции контуров
  let currentX = startX + firstCircuitOffset;
  const circuitPositionsX = circuits.map((circuit, index) => {
    const pos = currentX;
    if (index < circuits.length - 1) {
      currentX += Number(circuit.spacingToNext) || circuitSpacing;
    }
    return pos;
  });

  // Генерируем каждый контур
  circuits.forEach((circuit, index) => {
    const circuitX = circuitPositionsX[index];
    
    // Если это сквозной дополнительный выход, модифицируем параметры контура
    let modifiedCircuit = { ...circuit };
    
    // Определяем направление контура (приоритет у circuit.direction)
    const actualDirection = circuit.direction || direction;
    
    if (isAdditionalOutlet && outletType === 'through') {
      // Для сквозных выходов увеличиваем длину на межосевое расстояние
      // Это аналогично длинным фитингам контуров
      const isFlange = circuit.fittingId?.startsWith('Flanec_');
      const isNakGaika = circuit.fittingId?.startsWith('Nak_Gaika_');
      const isShaibaNarRezba = circuit.fittingId?.startsWith('Shaib_Nar_Rezba_');
      const isSpecialFitting = isFlange || isNakGaika || isShaibaNarRezba;
      
      // Получаем базовую длину короткого фитинга
      let baseLength = 80; // дефолт
      
      if (isSpecialFitting) {
        // Видимый вылет: у гайки и шайбы это 32/54, а не старые 40/58
        baseLength = visibleSpecialStubLength(circuit.fittingId, circuit.flangePipeLength, shape);
        if (isNakGaika || isShaibaNarRezba) {
          modifiedCircuit.lengthIsVisible = true;
        }
      } else if (circuit.fittingLength !== null && circuit.fittingLength !== undefined) {
        baseLength = Number(circuit.fittingLength);
      } else if (circuit.fittingId) {
        const fittingInfo = fittingsData[circuit.fittingId];
        if (fittingInfo) {
          baseLength = fittingInfo.length;
        }
      } else if (circuit.length) {
        baseLength = Number(circuit.length);
      }
      
      // Увеличиваем длину на межосевое расстояние
      const throughLength = baseLength + pipeSpacing;
      
      // Модифицируем контур для сквозного выхода
      if (isSpecialFitting) {
        modifiedCircuit.flangePipeLength = throughLength;
      } else {
        modifiedCircuit.fittingLength = throughLength;
        modifiedCircuit.length = throughLength;
        modifiedCircuit.fittingLengthIsProduction = circuit.fittingLength == null;
      }
      
      // Сквозные выходы аналогичны длинным патрубкам — нужны 3 отверстия
      modifiedCircuit.isLongCircuit = true;
      
      // Для сквозных выходов нужно сместить startY, чтобы верх был на уровне короткого
      // Аналогично длинным фитингам в парных контурах
    }
    
    const result = generateCircuitFull({
      circuit: modifiedCircuit,
      circuitX,
      startY: isAdditionalOutlet && outletType === 'through' && actualDirection === 'up'
        ? startY + pipeSpacing  // Смещаем вниз для верхнего выхода
        : isAdditionalOutlet && outletType === 'through' && actualDirection === 'down'
        ? startY - pipeSpacing  // Смещаем вверх для нижнего выхода
        : startY,
      mainPipeId,
      mainPipeDiameter,
      mainPipeThickness, // Передаем реальную толщину коллектора
      mainPipeProfile, // передаем профиль для квадратных
      pipeSpec, // передаем спецификацию для квадратных
      index,
      direction: actualDirection, // Используем актуальное направление
      shape, // форма коллектора
      isAdditionalOutlet, // передаем флаг дополнительного выхода
      isThroughHole, // передаем флаг сквозного отверстия
    });

    cylinders.push(...result.cylinders);
    fittings.push(...result.fittings);
    welds.push(...result.welds);
    
    if (result.circuitObjData) {
      circuitObjects.push(result.circuitObjData);
    }
  });

  return { cylinders, fittings, welds, circuitObjects };
};

/**
 * Генерирует парный контур (два патрубка) для распределительного коллектора
 * Оба патрубка на одном коллекторе, смещены по X на circuitSpacing
 * Для верхнего коллектора (up): первый короткий, второй длинный
 * Для нижнего коллектора (down): первый длинный, второй короткий
 * @param {Object} params - параметры
 * @returns {Object} - объект с cylinders, fittings, welds, circuitObjData
 */
export const generatePairedCircuit = (params) => {
  const {
    circuit,
    circuitX,          // X координата первого патрубка
    startY,            // Y координата коллектора
    mainPipeId,
    mainPipeDiameter,
    mainPipeThickness, // Реальная толщина основной трубы
    mainPipeProfile,
    pipeSpec,
    index,
    direction = 'up',
    pipeSpacing,       // межосевое расстояние между коллекторами (для длинного патрубка)
    circuitSpacing,    // межосевое расстояние внутри контура (между двумя патрубками)
    swapOrder = false, // поменять местами короткий и длинный патрубки
    shape = 'round',   // форма коллектора
  } = params;

  const cylinders = [];
  const fittings = [];
  const welds = [];

  // Определяем, это фланец/гайка/шайба или обычный фитинг
  const isFlange = circuit.fittingId?.startsWith('Flanec_');
  const isNakGaika = circuit.fittingId?.startsWith('Nak_Gaika_');
  const isShaibaNarRezba = circuit.fittingId?.startsWith('Shaib_Nar_Rezba_');
  const isSpecialFitting = isFlange || isNakGaika || isShaibaNarRezba;
  
  // Получаем длину короткого патрубка
  let shortLength = 80; // значение по умолчанию
  
  if (isSpecialFitting) {
    // Видимый вылет короткого. У гайки и шайбы пустое поле — 32 мм (круг) или 54 мм (квадрат).
    // Длинный считается от этого вылета, иначе его торец уезжает на 8 или 4 мм.
    shortLength = visibleSpecialStubLength(circuit.fittingId, circuit.flangePipeLength, shape);
  } else if (circuit.fittingLength !== null && circuit.fittingLength !== undefined) {
    shortLength = Number(circuit.fittingLength);
  } else if (circuit.fittingId) {
    const fittingInfo = fittingsData[circuit.fittingId];
    if (fittingInfo) {
      shortLength = fittingInfo.length;
    }
  } else if (circuit.length) {
    shortLength = Number(circuit.length);
  }
  
  const longLength = shortLength + pipeSpacing;

  // Короткий патрубок
  // Для квадратных профилей без явно заданного пользователем fittingLength сбрасываем его.
  // Если пользователь задал вылет контура (fittingLength != null) — сохраняем его.
  const hasUserFittingLength = circuit.fittingLength !== null && circuit.fittingLength !== undefined;
  const shouldResetFittingLength = shape === 'square' && !isSpecialFitting && circuit.fittingId && !hasUserFittingLength;
  const shortCircuit = {
    type: circuit.fittingId ? 'fitting' : 'pipe',
    fittingId: circuit.fittingId,
    flangePipeLength: circuit.flangePipeLength,
    fittingLength: shouldResetFittingLength ? null : circuit.fittingLength,
    length: circuit.length,
    diameter: circuit.diameter,
    isLongCircuit: false, // IGES: короткий патрубок (1 отверстие)
  };

  // Длинный патрубок
  const longCircuit = {
    type: circuit.fittingId ? 'fitting' : 'pipe',
    fittingId: circuit.fittingId,
    // Для фланцев/гаек/шайб увеличиваем flangePipeLength, для остальных - fittingLength
    flangePipeLength: isSpecialFitting ? longLength : circuit.flangePipeLength,
    // Длина уже видимая: 32 + межосевое, а не 40 + межосевое
    lengthIsVisible: isNakGaika || isShaibaNarRezba,
    fittingLength: isSpecialFitting ? circuit.fittingLength : longLength,
    fittingLengthIsProduction: isSpecialFitting ? false : !hasUserFittingLength,
    length: longLength,
    diameter: circuit.diameter,
    isLongCircuit: true, // IGES: длинный патрубок (3 отверстия)
  };

  // Смещённая стартовая позиция для длинного патрубка
  const longStartY = direction === 'up' 
    ? startY + longLength - shortLength  // Смещаем вниз, чтобы верх был на уровне короткого
    : startY - longLength + shortLength; // Смещаем вверх, чтобы низ был на уровне короткого

  let firstResult, secondResult;

  // ВАЖНО: При swap меняем только длины патрубков, но НЕ их позиции X!
  // Это нужно, чтобы размерные линии между контурами не менялись при swap.
  // Позиции всегда фиксированы:
  // - Первый патрубок (index*2) всегда на circuitX
  // - Второй патрубок (index*2+1) всегда на circuitX + circuitSpacing
  
  if (direction === 'up') {
    // Верхний коллектор
    if (!swapOrder) {
      // Обычный порядок: первый короткий, второй длинный
      firstResult = generateCircuitFull({
        circuit: shortCircuit,
        circuitX,
        startY,
        mainPipeId,
        mainPipeDiameter,
        mainPipeThickness, // Передаем реальную толщину коллектора
        mainPipeProfile,
        pipeSpec,
        index: index * 2,
        direction,
        shape,
      });

      secondResult = generateCircuitFull({
        circuit: longCircuit,
        circuitX: circuitX + circuitSpacing,
        startY: longStartY,
        mainPipeId,
        mainPipeDiameter,
        mainPipeThickness, // Передаем реальную толщину коллектора
        mainPipeProfile,
        pipeSpec,
        index: index * 2 + 1,
        direction,
        shape,
      });
    } else {
      // Обратный порядок: первый длинный, второй короткий
      // НО позиции X остаются те же!
      firstResult = generateCircuitFull({
        circuit: longCircuit,
        circuitX,
        startY: longStartY,
        mainPipeId,
        mainPipeDiameter,
        mainPipeThickness, // Передаем реальную толщину коллектора
        mainPipeProfile,
        pipeSpec,
        index: index * 2,
        direction,
        shape,
      });

      secondResult = generateCircuitFull({
        circuit: shortCircuit,
        circuitX: circuitX + circuitSpacing,
        startY,
        mainPipeId,
        mainPipeDiameter,
        mainPipeThickness, // Передаем реальную толщину коллектора
        mainPipeProfile,
        pipeSpec,
        index: index * 2 + 1,
        direction,
        shape,
      });
    }
  } else {
    // Нижний коллектор
    if (!swapOrder) {
      // Обычный порядок: первый длинный, второй короткий
      firstResult = generateCircuitFull({
        circuit: longCircuit,
        circuitX,
        startY: longStartY,
        mainPipeId,
        mainPipeDiameter,
        mainPipeThickness, // Передаем реальную толщину коллектора
        mainPipeProfile,
        pipeSpec,
        index: index * 2,
        direction,
        shape,
      });

      secondResult = generateCircuitFull({
        circuit: shortCircuit,
        circuitX: circuitX + circuitSpacing,
        startY,
        mainPipeId,
        mainPipeDiameter,
        mainPipeThickness, // Передаем реальную толщину коллектора
        mainPipeProfile,
        pipeSpec,
        index: index * 2 + 1,
        direction,
        shape,
      });
    } else {
      // Обратный порядок: первый короткий, второй длинный
      // НО позиции X остаются те же!
      firstResult = generateCircuitFull({
        circuit: shortCircuit,
        circuitX,
        startY,
        mainPipeId,
        mainPipeDiameter,
        mainPipeThickness, // Передаем реальную толщину коллектора
        mainPipeProfile,
        pipeSpec,
        index: index * 2,
        direction,
        shape,
      });

      secondResult = generateCircuitFull({
        circuit: longCircuit,
        circuitX: circuitX + circuitSpacing,
        startY: longStartY,
        mainPipeId,
        mainPipeDiameter,
        mainPipeThickness, // Передаем реальную толщину коллектора
        mainPipeProfile,
        pipeSpec,
        index: index * 2 + 1,
        direction,
        shape,
      });
    }
  }

  cylinders.push(...firstResult.cylinders, ...secondResult.cylinders);
  fittings.push(...firstResult.fittings, ...secondResult.fittings);
  welds.push(...firstResult.welds, ...secondResult.welds);

  // Возвращаем данные обоих патрубков для размерных линий
  // Определяем, какой патрубок короткий, а какой длинный, с учётом swapOrder
  let shortCircuitData, longCircuitData;
  
  if (direction === 'up') {
    // Для верхнего коллектора
    if (!swapOrder) {
      // Обычный порядок: первый короткий, второй длинный
      shortCircuitData = firstResult.circuitObjData;
      longCircuitData = secondResult.circuitObjData;
    } else {
      // Обратный порядок: первый длинный, второй короткий
      shortCircuitData = secondResult.circuitObjData;
      longCircuitData = firstResult.circuitObjData;
    }
  } else {
    // Для нижнего коллектора
    if (!swapOrder) {
      // Обычный порядок: первый длинный, второй короткий
      shortCircuitData = secondResult.circuitObjData;
      longCircuitData = firstResult.circuitObjData;
    } else {
      // Обратный порядок: первый короткий, второй длинный
      shortCircuitData = firstResult.circuitObjData;
      longCircuitData = secondResult.circuitObjData;
    }
  }
  
  const circuitObjData = {
    shortCircuit: shortCircuitData,
    longCircuit: longCircuitData,
  };

  return { cylinders, fittings, welds, circuitObjData };
};

/**
 * Генерирует парные контуры для распределительного коллектора
 * @param {Object} params - параметры
 * @returns {Object} - объект с cylinders, fittings, welds, supplyCircuitObjects, returnCircuitObjects
 */
export const generatePairedCircuitsFull = (params) => {
  const {
    supplyCircuits,
    returnCircuits,
    supplyStartX, // начало для подающего коллектора
    returnStartX, // начало для обратного коллектора
    supplyY,
    returnY,
    supplyPipeId,
    returnPipeId,
    supplyPipeDiameter,
    returnPipeDiameter,
    supplyPipeThickness, // Реальная толщина подающего коллектора
    returnPipeThickness, // Реальная толщина обратного коллектора
    supplyPipeProfile, // профиль для квадратных (подающий)
    returnPipeProfile, // профиль для квадратных (обратный)
    supplyPipeSpec, // спецификация для квадратных (подающий)
    returnPipeSpec, // спецификация для квадратных (обратный)
    supplyFirstCircuitOffset, // отступ первого контура для подачи
    returnFirstCircuitOffset, // отступ первого контура для обратки
    supplySpacingBetweenCircuits,
    returnSpacingBetweenCircuits,
    pipeSpacing, // межосевое расстояние между коллекторами
    shape = 'round', // форма коллектора (для правильных default/override значений)
  } = params;

  const cylinders = [];
  const fittings = [];
  const welds = [];
  const supplyCircuitObjects = [];
  const returnCircuitObjects = [];

  // Вычисляем позиции контуров для подающего коллектора
  let currentSupplyX = supplyStartX + supplyFirstCircuitOffset;
  
  const supplyCircuitPositionsX = supplyCircuits.map((circuit, index) => {
    const pos = currentSupplyX;
    if (index < supplyCircuits.length - 1) {
      const circuitInternalSpacing = Number(circuit.circuitSpacing) || 125; // межосевое внутри контура
      const spacingToNextCircuit = Number(circuit.spacingToNext) || supplySpacingBetweenCircuits || 125;
      currentSupplyX += circuitInternalSpacing + spacingToNextCircuit;
    }
    return pos;
  });
  
  // Вычисляем позиции контуров для обратного коллектора
  let currentReturnX = returnStartX + returnFirstCircuitOffset;
  const returnCircuitPositionsX = returnCircuits.map((circuit, index) => {
    const pos = currentReturnX;
    if (index < returnCircuits.length - 1) {
      const circuitInternalSpacing = Number(circuit.circuitSpacing) || 125; // межосевое внутри контура
      const spacingToNextCircuit = Number(circuit.spacingToNext) || returnSpacingBetweenCircuits || 125;
      currentReturnX += circuitInternalSpacing + spacingToNextCircuit;
    }
    return pos;
  });

  // Генерируем контуры подачи
  supplyCircuits.forEach((circuit, index) => {
    const supplyCircuitX = supplyCircuitPositionsX[index];
    
    // Подающий контур (вверх) - два патрубка на одном коллекторе
    const supplyCircuit = {
      fittingId: circuit.fitting,
      flangePipeLength: circuit.flangePipeLength,
      fittingLength: circuit.fittingLength,
    };
    
    const swapOrder = circuit.swapOrder || false;
    const circuitInternalSpacing = Number(circuit.circuitSpacing) || 50;
    
    const supplyResult = generatePairedCircuit({
      circuit: supplyCircuit,
      circuitX: supplyCircuitX,
      startY: supplyY,
      mainPipeId: supplyPipeId,
      mainPipeDiameter: supplyPipeDiameter,
      mainPipeThickness: supplyPipeThickness, // Реальная толщина коллектора
      mainPipeProfile: supplyPipeProfile,
      pipeSpec: supplyPipeSpec,
      index,
      direction: 'up',
      pipeSpacing, // межосевое расстояние между коллекторами
      circuitSpacing: circuitInternalSpacing, // межосевое внутри контура
      swapOrder, // поменять местами короткий и длинный патрубки
      shape, // форма коллектора
    });

    cylinders.push(...supplyResult.cylinders);
    fittings.push(...supplyResult.fittings);
    welds.push(...supplyResult.welds);
    
    if (supplyResult.circuitObjData) {
      // Добавляем оба патрубка в массив объектов В ПОРЯДКЕ ИХ ПОЗИЦИЙ X (слева направо)
      // Первый патрубок всегда на circuitX, второй на circuitX + circuitSpacing
      // При swap меняется только тип (короткий/длинный), но не позиция
      
      // Для верхнего коллектора (up):
      // - Без swap: первый=короткий (X=circuitX), второй=длинный (X=circuitX+spacing)
      // - Со swap: первый=длинный (X=circuitX), второй=короткий (X=circuitX+spacing)
      
      if (!swapOrder) {
        // Обычный порядок: короткий, длинный
        if (supplyResult.circuitObjData.shortCircuit) {
          supplyCircuitObjects.push({ ...supplyResult.circuitObjData.shortCircuit, isShort: true });
        }
        if (supplyResult.circuitObjData.longCircuit) {
          supplyCircuitObjects.push({ ...supplyResult.circuitObjData.longCircuit, isShort: false });
        }
      } else {
        // Swap: длинный, короткий (но позиции X те же!)
        if (supplyResult.circuitObjData.longCircuit) {
          supplyCircuitObjects.push({ ...supplyResult.circuitObjData.longCircuit, isShort: false });
        }
        if (supplyResult.circuitObjData.shortCircuit) {
          supplyCircuitObjects.push({ ...supplyResult.circuitObjData.shortCircuit, isShort: true });
        }
      }
    }
  });
  
  // Генерируем контуры обратки
  returnCircuits.forEach((circuit, index) => {
    const returnCircuitX = returnCircuitPositionsX[index];
    
    // Обратный контур (вниз) - два патрубка на одном коллекторе
    const returnCircuit = {
      fittingId: circuit.fitting,
      flangePipeLength: circuit.flangePipeLength,
      fittingLength: circuit.fittingLength,
    };
    
    const swapOrder = circuit.swapOrder || false;
    const circuitInternalSpacing = Number(circuit.circuitSpacing) || 50;
    
    const returnResult = generatePairedCircuit({
      circuit: returnCircuit,
      circuitX: returnCircuitX,
      startY: returnY,
      mainPipeId: returnPipeId,
      mainPipeDiameter: returnPipeDiameter,
      mainPipeThickness: returnPipeThickness, // Реальная толщина коллектора
      mainPipeProfile: returnPipeProfile,
      pipeSpec: returnPipeSpec,
      index,
      direction: 'down',
      pipeSpacing, // межосевое расстояние между коллекторами
      circuitSpacing: circuitInternalSpacing, // межосевое внутри контура
      swapOrder, // поменять местами короткий и длинный патрубки
      shape, // форма коллектора
    });

    cylinders.push(...returnResult.cylinders);
    fittings.push(...returnResult.fittings);
    welds.push(...returnResult.welds);
    
    if (returnResult.circuitObjData) {
      // Добавляем оба патрубка в массив объектов В ПОРЯДКЕ ИХ ПОЗИЦИЙ X (слева направо)
      // Первый патрубок всегда на circuitX, второй на circuitX + circuitSpacing
      // При swap меняется только тип (короткий/длинный), но не позиция
      
      // Для нижнего коллектора (down):
      // - Без swap: первый=длинный (X=circuitX), второй=короткий (X=circuitX+spacing)
      // - Со swap: первый=короткий (X=circuitX), второй=длинный (X=circuitX+spacing)
      
      if (!swapOrder) {
        // Обычный порядок: длинный, короткий
        if (returnResult.circuitObjData.longCircuit) {
          returnCircuitObjects.push({ ...returnResult.circuitObjData.longCircuit, isShort: false });
        }
        if (returnResult.circuitObjData.shortCircuit) {
          returnCircuitObjects.push({ ...returnResult.circuitObjData.shortCircuit, isShort: true });
        }
      } else {
        // Swap: короткий, длинный (но позиции X те же!)
        if (returnResult.circuitObjData.shortCircuit) {
          returnCircuitObjects.push({ ...returnResult.circuitObjData.shortCircuit, isShort: true });
        }
        if (returnResult.circuitObjData.longCircuit) {
          returnCircuitObjects.push({ ...returnResult.circuitObjData.longCircuit, isShort: false });
        }
      }
    }
  });

  // Вычисляем позиции контуров для метаданных IGES
  const supplyCircuitPositions = [];
  let supplyCurrentX = supplyFirstCircuitOffset;
  supplyCircuits.forEach((circuit, index) => {
    const circuitInternalSpacing = Number(circuit.circuitSpacing) || 125; // межосевое внутри контура
    // Первый патрубок контура
    supplyCircuitPositions.push({ x: supplyStartX + supplyCurrentX, direction: 'up' });
    // Второй патрубок контура
    supplyCircuitPositions.push({ x: supplyStartX + supplyCurrentX + circuitInternalSpacing, direction: 'up' });
    
    if (index < supplyCircuits.length - 1) {
      const spacingToNextCircuit = Number(circuit.spacingToNext) || supplySpacingBetweenCircuits || 125;
      supplyCurrentX += circuitInternalSpacing + spacingToNextCircuit;
    }
  });

  const returnCircuitPositions = [];
  let returnCurrentX = returnFirstCircuitOffset;
  returnCircuits.forEach((circuit, index) => {
    const circuitInternalSpacing = Number(circuit.circuitSpacing) || 125; // межосевое внутри контура
    // Первый патрубок контура
    returnCircuitPositions.push({ x: returnStartX + returnCurrentX, direction: 'down' });
    // Второй патрубок контура
    returnCircuitPositions.push({ x: returnStartX + returnCurrentX + circuitInternalSpacing, direction: 'down' });
    
    if (index < returnCircuits.length - 1) {
      const spacingToNextCircuit = Number(circuit.spacingToNext) || returnSpacingBetweenCircuits || 125;
      returnCurrentX += circuitInternalSpacing + spacingToNextCircuit;
    }
  });

  return {
    cylinders,
    fittings,
    welds,
    supplyCircuitObjects,
    returnCircuitObjects,
    supplyCircuitPositions,
    returnCircuitPositions,
  };
};

